import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FieldLabel } from "@/components/FieldLabel";
import SearchInput from "@/components/SearchInput";
import CopyableSecret from "@/components/CopyableSecret";
import { useSearch } from "@/lib/search";
import Paginator, { usePaged } from "@/components/Paginator";
import { formatDateTime, plural } from "@/lib/utils";
import { Plus, ShieldAlert, KeyRound } from "lucide-react";
import { toast } from "sonner";

// The roles worth handing out from here. "Employee" is absent on purpose — an employee
// login is made from the Employees screen, so it stays attached to the person it belongs
// to. "HR" is the legacy spelling of HR Manager and is not offered to anyone new.
const ROLES = ["Admin", "HR Manager", "Department Head", "Unit Coordinator", "Viewer"];

const ROLE_NOTE = {
  Admin: "Everything, including this screen and the audit log.",
  // There is no approval step, and nothing narrows these two roles to a unit: security.py gates on
  // the role alone. A note promising either tells an Admin something the system does not do.
  "HR Manager": "Runs the training register: create, schedule, mark attendance, issue certificates.",
  "Department Head": "Same access as HR Manager, across every unit.",
  "Unit Coordinator": "Same access as HR Manager, across every unit.",
  Viewer: "Reads every screen and downloads reports. Changes nothing.",
};

const USER_FIELDS = ["name", "email", "role"];
const BLANK = { name: "", email: "", password: "", role: "Viewer" };
// A stable empty list to search while the accounts have not arrived.
const EMPTY = [];

export default function Users() {
  const { user } = useAuth();
  // null until /users answers. Starting at [] printed "0 accounts" and "No accounts yet.",
  // and a failed load left both standing after its toast had gone — in front of an Admin
  // signed in on one of those accounts.
  const [users, setUsers] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  // Who we are about to reset, and the password that came back. Held in state only long
  // enough to be read off the screen — it is never fetched again and never stored.
  const [resetting, setResetting] = useState(null);
  const [issued, setIssued] = useState(null);

  // Numbered, because a create or a reset reloads while an earlier load or a Try again may
  // still be out, and whichever answered last decided what the screen said: a slow failure
  // could put the error over a list that had just loaded.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoadError(false);
    try {
      const r = await api.get("/users");
      if (seq !== loadSeq.current) return;
      // A 200 that is not the list (an HTML page answering in its place) is a failure, not
      // a set of accounts: taken as it was, the page showed no sign that anything went wrong.
      if (!Array.isArray(r.data)) throw new Error("unexpected response");
      setUsers(r.data);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      // The last good list stays up; the error row above it says it may be out of date.
      setLoadError(true);
      toast.error(formatApiError(e.response?.data?.detail, "Could not load users"));
    }
  }, []);
  useEffect(() => { if (isAdmin(user)) load(); }, [user, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success(`${form.name} can now sign in`);
      setOpen(false);
      setForm(BLANK);
      load();
    } catch (e) {
      // Stays open with what was typed: the refusals here are a taken email or a short
      // password, and both are things you fix in the form you are already looking at.
      toast.error(formatApiError(e.response?.data?.detail, "Could not create the account"));
    }
    setSaving(false);
  };

  // There is no email in this installation, so there is no reset link and no self-serve
  // way back in. Until this existed there was no route that could change somebody else's
  // password at all: an HR Manager who forgot theirs was out of the system for good and
  // the only remedy was psql on the server.
  const resetPassword = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-password`);
      setIssued({ ...r.data, name: u.name });
      setResetting(null);
      load();
    } catch (e) {
      setResetting(null);
      toast.error(formatApiError(e.response?.data?.detail, "Could not reset the password"));
    }
  };

  const filtered = useSearch(users || EMPTY, q, USER_FIELDS);
  const paged = usePaged(filtered);

  if (!isAdmin(user)) {
    return (
      <Card className="p-8 border border-border text-center" data-testid="users-denied">
        <ShieldAlert className="w-6 h-6 mx-auto text-muted-foreground" />
        <div className="font-heading font-semibold mt-3">Administrators only</div>
        <p className="text-sm text-muted-foreground mt-1">Only an Admin can see or create logins.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Access</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Users &amp; Roles</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {Array.isArray(users) && `${plural(users.length, "account")} that can sign in. `}Employee logins are made
            from the Employees screen and appear here once created.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add user
        </Button>
      </div>

      <Card className="p-4 border border-border">
        <SearchInput value={q} onChange={setQ} testid="user-search"
                     placeholder="Search name, email or role..." />
      </Card>

      <Card className="border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">State</th>
                <th className="px-4 py-2 font-medium">Added</th>
                <th className="px-4 py-2 font-medium sr-only">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Whenever the last load failed, not only while nothing has loaded: a reload
                  that fails after a create or a reset leaves the previous accounts standing,
                  and with the toast gone they read as current. */}
              {loadError && (
                <tr data-testid="users-load-error"><td colSpan={6} className="px-4 py-6 text-center">
                  <div className="text-destructive-text">Could not load accounts.</div>
                  {users?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      The rows below are the accounts as they last loaded, and may be out of date.
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="mt-3" onClick={load}
                          data-testid="users-retry">Try again</Button>
                </td></tr>
              )}
              {/* With nothing loaded, the error row above is the whole answer. */}
              {paged.slice.length === 0 && (!loadError || users?.length > 0) && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  {users === null ? "Loading..." : q ? `Nobody matches "${q}".` : "No accounts yet."}
                </td></tr>
              )}
              {paged.slice.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-${u.id}`}>
                  <td className="px-4 py-2 font-medium">
                    {u.name}
                    {u.employee_id && <span className="ml-2 text-xs text-muted-foreground">employee</span>}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-2">{u.role}</td>
                  <td className="px-4 py-2">
                    {u.disabled_at ? (
                      <Badge variant="outline" className="text-destructive-text border-destructive/40">
                        Disabled
                      </Badge>
                    ) : u.must_change_password ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        Password not set
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-emerald-700 dark:text-emerald-400 border-emerald-500/40">
                        Active
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{formatDateTime(u.created_at)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {/* Absent on your own row: resetting yourself would hand you a
                        temporary password and end the session you are reading it in.
                        Absent on a disabled account, which sign-in refuses anyway. */}
                    {u.id !== user.id && !u.disabled_at && (
                      <Button variant="ghost" size="sm" onClick={() => setResetting(u)}
                              data-testid={`reset-pw-${u.id}`}>
                        <KeyRound className="w-4 h-4 mr-1.5" /> Reset password
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginator paged={paged} label="accounts" testid="users-paginator" />
      </Card>

      {/* Asked before, not offered as an Undo: it ends whatever session that account is
          holding and there is no way to put the old password back. */}
      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Reset the password for {resetting?.name}?</DialogTitle>
            <DialogDescription>
              A one-time password is generated and shown to you once. They will have to set their
              own the next time they sign in.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Anything they are signed in to right now stops working immediately, and their old
            password stops working with it. Tell them the new one yourself — nothing is emailed.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
            <Button onClick={() => resetPassword(resetting)} data-testid="reset-pw-confirm">
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issued} onOpenChange={(v) => !v && setIssued(null)}>
        <DialogContent data-testid="reset-pw-result">
          <DialogHeader>
            <DialogTitle>New password for {issued?.name}</DialogTitle>
            <DialogDescription>Shown once. It cannot be retrieved afterwards.</DialogDescription>
          </DialogHeader>
          {issued && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-md bg-muted border border-border font-mono text-sm space-y-1">
                <CopyableSecret label="Email" value={issued.email} testid="reset-pw-email" />
                <CopyableSecret label="Password" value={issued.temporary_password} testid="reset-pw-value" />
              </div>
              <p className="text-muted-foreground">
                Read it out or hand it over in person. They are asked to set their own password the
                moment they sign in, so this one only has to survive the walk to their desk.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIssued(null)} data-testid="reset-pw-close">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>Add a user</DialogTitle>
            <DialogDescription>
              They sign in with this email and password. Tell them the password yourself — it is
              never shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <FieldLabel htmlFor="u-name" required>Name</FieldLabel>
              <Input id="u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" />
            </div>
            <div>
              <FieldLabel htmlFor="u-email" required>Email</FieldLabel>
              <Input id="u-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" />
            </div>
            <div>
              <FieldLabel htmlFor="u-pw" required>Password</FieldLabel>
              <Input id="u-pw" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-pw" />
            </div>
            <div>
              <FieldLabel htmlFor="u-role" required>Role</FieldLabel>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger id="u-role" data-testid="u-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="u-role-note">{ROLE_NOTE[form.role]}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="u-save">
              {saving ? "Creating..." : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
